import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Bookings.css";
import { SidebarContext } from "../../context/SidebarContext";
import { useContext } from "react";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import { config } from "../../config.js";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaEdit,
  FaImages,
  FaDownload,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaWhatsapp,
} from "react-icons/fa";
import CustomSelect from "../../components/CustomSelect/CustomSelect.jsx";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

const getCancelledBy = (booking) =>
  String(
    booking?.canceled?.cancelledBy || booking?.cancelled?.cancelledBy || "",
  ).toLowerCase();

// A driver rejection is an assignment retry state, not a cancelled trip. Once
// another driver is assigned, the booking continues normally even if an old
// record still contains the original driver-cancel marker.
const isDriverRetry = (booking) =>
  getCancelledBy(booking) === "driver" &&
  !booking?.approvedDriver?.driverId &&
  !booking?.hasStarted &&
  !booking?.hasEnded?.status;

const isTerminalCancellation = (booking) =>
  getCancelledBy(booking) !== "driver" &&
  Boolean(
    booking?.canceled?.reason ||
    booking?.canceled?.cancelledBy ||
    booking?.cancelled?.reason ||
    booking?.cancelled?.cancelledBy,
  );

const hasDriverReached = (booking) =>
  booking?.driverHasReached?.status === true;

function Bookings({ view = "upcoming" }) {
  const { isSidebar } = useContext(SidebarContext);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState({});
  const [loading, setLoading] = useState(true);
  const [driverLoading, setDriverLoading] = useState(true);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [downloadingPhoto, setDownloadingPhoto] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [search, setSearch] = useState("");
  const role = localStorage.getItem("adminRole");
  const adminCity =
    role === "sub-admin" ? localStorage.getItem("adminCity") : null;

  const bookingDate = (booking) => {
    const value =
      booking.bookingTime || booking.scheduledAt || booking.createdAt;
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? new Date(0) : date;
  };

  const visibleBookings = useMemo(() => {
    const isCompleted = (booking) => Boolean(booking.hasEnded?.status);
    const rows = bookings.filter((booking) => {
      // A driver rejection remains open for another driver through the same
      // 15-minute grace window used by the server cron.
      if (isDriverRetry(booking)) {
        return (
          view === "upcoming" &&
          bookingDate(booking).getTime() + 15 * 60 * 1000 > Date.now()
        );
      }
      return view === "cancelled"
        ? isTerminalCancellation(booking)
        : view === "completed"
          ? isCompleted(booking) && !isTerminalCancellation(booking)
          : view === "ongoing"
            ? booking.hasStarted === true &&
              !isCompleted(booking) &&
              !isTerminalCancellation(booking)
            : booking.hasStarted !== true &&
              !isCompleted(booking) &&
              !isTerminalCancellation(booking);
    });
    return [...rows].sort((a, b) =>
      view === "upcoming"
        ? bookingDate(a) - bookingDate(b)
        : bookingDate(b) - bookingDate(a),
    );
  }, [bookings, view]);

  const filteredBookings = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return visibleBookings;

    return visibleBookings.filter((booking) => {
      const values = [
        booking.booking_id,
        booking.userDetails?.userName,
        booking.userDetails?.phoneNumber,
        booking.approvedDriver?.driverName,
        booking.carType,
        booking.tripType,
        booking.tripSection,
        booking.payment?.paymentStatus,
        booking.payment?.paymentMode,
      ];

      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [visibleBookings, search]);

  const getBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config?.backEndBaseUrl}booking${adminCity ? `?adc=${adminCity.toLocaleLowerCase()}` : ""}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result.status && Array.isArray(result.bookings)) {
        // console.log(result.bookings);
        setBookings(result.bookings);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Fetching drivers failed:", error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getDrivers = useCallback(async () => {
    setDriverLoading(true);
    try {
      const response = await fetch(
        `${config?.backEndBaseUrl}driver/get${adminCity ? `?adc=${adminCity.toLocaleLowerCase()}` : ""}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-dashboard-request": "true",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result.status && Array.isArray(result.drivers)) {
        setDrivers(result.drivers);
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Fetching drivers failed:", error);
      setDrivers([]);
    } finally {
      setDriverLoading(false);
    }
  }, []);

  useEffect(() => {
    getBookings();
    getDrivers();
  }, [getBookings, getDrivers]);

  const driverOptions = useMemo(() => {
    return drivers.map((d) => ({
      label: d.userName,
      value: d.uid,
      rating: d.userRating?.rate || 0,
      isOnline: d.isOnline,
      isOnride: d.isOnride,
    }));
  }, [drivers]);

  const driverOptionsById = useMemo(() => {
    return new Map(driverOptions.map((option) => [option.value, option]));
  }, [driverOptions]);

  const assignDriverToBooking = async (booking, driver) => {
    try {
      const response = await fetch(
        `${config.backEndBaseUrl}booking/${booking.userId}/${booking.id}/assign-driver`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            driverId: driver.value,
            driverName: driver.label,
            driverRating: driver.rating,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        const conflictTime = result?.conflict?.bookingTime
          ? new Date(result.conflict.bookingTime).toLocaleString("en-IN")
          : "";
        throw new Error(
          conflictTime
            ? `${result.details || "Driver has a conflicting booking."} Conflicting booking time: ${conflictTime}.`
            : result.details || result.message || "Failed to assign driver",
        );
      }

      getBookings();
      toast.success("Driver assigned successfully.");
    } catch (error) {
      console.error("Assign driver failed:", error);
      toast.error(error.message || "Failed to assign driver");
    }
  };

  const cancelBookingByAdmin = async (booking) => {
    const reason = window.prompt(
      "Cancellation reason for the booking record:",
      "Cancelled by administrator after review.",
    );
    if (reason === null) return;

    try {
      const response = await fetch(
        `${config.backEndBaseUrl}booking/${booking.userId}/${booking.id}/cancel-by-admin`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.details || result.message || "Failed to cancel booking",
        );
      getBookings();
    } catch (error) {
      console.error("Admin booking cancellation failed:", error);
      window.alert(error.message || "Failed to cancel booking");
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return "0 min";

    const totalMinutes = Math.round(duration);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }

    return `${minutes} min`;
  };

  const StarRating = ({ rating = 0 }) => {
    const totalStars = 5;

    const safeRating = Math.max(0, Math.min(5, Number(rating)));
    const fullStars = Math.floor(safeRating);
    const hasHalfStar = safeRating % 1 >= 0.5;
    const emptyStars = totalStars - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <span className="d-flex align-items-center gap-1">
        {/* Full Stars */}
        {[...Array(fullStars)].map((_, i) => (
          <FaStar key={`full-${i}`} style={{ color: "#ffc107" }} />
        ))}

        {/* Half Star */}
        {hasHalfStar && (
          <FaStarHalfAlt key="half" style={{ color: "#ffc107" }} />
        )}

        {/* Empty Stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <FaRegStar key={`empty-${i}`} style={{ color: "#6c757d" }} />
        ))}
      </span>
    );
  };

  const openWhatsApp = () => {
    if (!selectedBooking) return;

    const phone = String(
      selectedBooking.userDetails?.phoneNumber || "",
    ).replace(/\D/g, "");

    if (!phone) {
      window.alert("User phone number is not available.");
      return;
    }

    // If Indian 10-digit number, add India country code
    const whatsappNumber = phone.length === 10 ? `91${phone}` : phone;

    const bookingId = selectedBooking.booking_id || "-";
    const userName = selectedBooking.userDetails?.userName || "Customer";
    const driverName =
      selectedBooking.approvedDriver?.driverName || "Not assigned";

    // const photos = Array.isArray(selectedBooking.carPhotos)
    //   ? selectedBooking.carPhotos
    //   : [];

    let message =
      `Hello ${userName},\n\n` +
      `We're contacting you regarding the complaint you raised with us.\n\n` +
      `Booking ID: ${bookingId}\n` +
      `Driver: ${driverName}\n\n`;

    // if (photos.length) {
    //   message += "Car Photos:\n";

    //   photos.forEach((photo, index) => {
    //     if (photo?.path) {
    //       message += `${index + 1}. ${photo.path}\n`;
    //     }
    //   });
    // }

    message += "\nThank you.";

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const openPhotoModal = (booking) => {
    if (!booking?.carPhotos?.length) return;

    setSelectedBooking(booking);
    setSelectedPhotoIndex(0);
    setPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedBooking(null);
    setSelectedPhotoIndex(0);
  };

  const nextPhoto = () => {
    if (!selectedBooking?.carPhotos?.length) return;

    setSelectedPhotoIndex((prev) =>
      prev === selectedBooking.carPhotos.length - 1 ? 0 : prev + 1,
    );
  };

  const previousPhoto = () => {
    if (!selectedBooking?.carPhotos?.length) return;

    setSelectedPhotoIndex((prev) =>
      prev === 0 ? selectedBooking.carPhotos.length - 1 : prev - 1,
    );
  };

  const downloadPhoto = async (photo, index) => {
    if (!photo?.path) return;

    try {
      setDownloadingPhoto(index);

      const response = await fetch(photo.path);

      if (!response.ok) {
        throw new Error("Failed to download image");
      }

      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = photo.name || `car-photo-${index + 1}.jpg`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Photo download failed:", error);

      // Fallback: open image directly
      window.open(photo.path, "_blank");
    } finally {
      setDownloadingPhoto(null);
    }
  };

  const downloadAllPhotos = async () => {
    if (!selectedBooking?.carPhotos?.length) return;

    try {
      setDownloadingAll(true);

      for (let i = 0; i < selectedBooking.carPhotos.length; i++) {
        await downloadPhoto(selectedBooking.carPhotos[i], i);

        // Small delay so browser doesn't block multiple downloads
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error) {
      console.error("Download all photos failed:", error);
    } finally {
      setDownloadingAll(false);
    }
  };

  const columns = useMemo(
    () => [
      // Booking ID
      {
        accessorKey: "booking_id",
        header: "Booking ID",
        Cell: ({ row }) => (
          <span className="fw-semibold text-primary">
            {row.original.booking_id}
          </span>
        ),
      },

      // User Info
      {
        accessorKey: "userDetails.userName",
        header: "User",
        Cell: ({ row }) => {
          const user = row.original.userDetails;
          return (
            <div>
              <div className="fw-semibold">{user?.userName || "N/A"}</div>
              <small className="text-muted">{user?.phoneNumber}</small>
            </div>
          );
        },
      },

      // Booking Time
      {
        accessorKey: "bookingTime",
        header: "Booking Time",
        sortingFn: (a, b) => bookingDate(a.original) - bookingDate(b.original),
        Cell: ({ row }) => {
          const date = bookingDate(row.original);
          return (
            <div className="booking-time">
              <strong>
                {date.toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </strong>
              <small>
                {date.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </small>
            </div>
          );
        },
      },

      // Trip Info
      {
        header: "Trip",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.tripType}</div>
            <small className="text-muted">{row.original.tripSection}</small>
          </div>
        ),
      },

      // Car Type
      {
        accessorKey: "carType",
        header: "Car",
      },

      // Driver Info
      {
        header: "Driver",
        Cell: ({ row }) => {
          const booking = row.original;
          const driver = booking.approvedDriver;
          const isPending =
            !isTerminalCancellation(booking) &&
            !booking?.hasEnded?.status &&
            booking?.hasStarted !== true;

          const isEditing = editingRowId === booking.id;
          const selectedValue =
            selectedDriver[booking.id] ||
            (driver?.driverId && driverOptionsById.get(driver.driverId)) ||
            (driver?.driverName
              ? {
                  label: driver.driverName,
                  value: driver.driverId,
                  rating: driver.driverRating ?? 0,
                }
              : null);

          if (isEditing) {
            return (
              <CustomSelect
                key={booking.id}
                options={driverOptions}
                value={selectedValue}
                placeholder="Select Driver"
                renderOption={(option) => {
                  let label = "Offline";
                  let color = "#dc3545"; // red

                  if (option.isOnride) {
                    label = "Ongoing";
                    color = "#e0a800"; // dark yellow
                  } else if (option.isOnline) {
                    label = "Online";
                    color = "#198754"; // green
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      {/* Driver Name */}
                      <span style={{ fontWeight: 500 }}>{option.label}</span>

                      {/* STATUS TAG */}
                      <span
                        style={{
                          fontSize: "11px",
                          padding: "3px 10px",
                          borderRadius: "999px",
                          border: `1px solid ${color}`,
                          color: color,
                          backgroundColor: `${color}15`, // soft background
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                }}
                onChange={(selected) => {
                  setSelectedDriver((prev) => ({
                    ...prev,
                    [booking.id]: selected,
                  }));

                  assignDriverToBooking(booking, selected);
                  setEditingRowId(null);
                }}
              />
            );
          }

          return (
            <div className="d-flex align-items-center gap-2">
              <div>
                {driver?.driverName ? (
                  <>
                    <div className="fw-semibold">{driver.driverName}</div>
                    <small className="text-muted">
                      {StarRating({ rating: driver.driverRating ?? 0 })}
                      {driver.driverRating
                        ? ` ${driver.driverRating}`
                        : "No rating"}{" "}
                      | {driver.ratingCount || 0} reviews
                    </small>
                  </>
                ) : (
                  <span className="text-muted">Not Assigned</span>
                )}
              </div>

              {isPending && (
                <FaEdit
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => setEditingRowId(booking.id)}
                />
              )}
            </div>
          );
        },
      },

      {
        header: "Photos",
        accessorKey: "carPhotos",
        enableSorting: false,
        Cell: ({ row }) => {
          const booking = row.original;
          const photos = Array.isArray(booking.carPhotos)
            ? booking.carPhotos
            : [];

          if (!photos.length) {
            return <span className="booking-no-photos">No photos</span>;
          }

          return (
            <button
              type="button"
              className="booking-photo-btn"
              onClick={() => openPhotoModal(booking)}
            >
              <span className="booking-photo-icon">
                <FaImages />
              </span>

              <span>
                {photos.length} {photos.length === 1 ? "Photo" : "Photos"}
              </span>
            </button>
          );
        },
      },

      // Route Info
      {
        header: "Distance / Duration",
        Cell: ({ row }) => {
          const route = row.original.routeInfo;
          return (
            <div>
              <div>{route?.distance?.toFixed(2) ?? 0} km</div>
              <small className="text-muted">
                {formatDuration(route?.duration)}
              </small>
            </div>
          );
        },
      },

      // Amount
      {
        header: "Amount",
        Cell: ({ row }) => (
          <div>
            <div>
              ₹ {row.original.payableAmount || row.original.bookingAmount}
            </div>
            <small className="text-muted">
              Base: ₹ {row.original.fareBreakdown?.baseRate}
            </small>
          </div>
        ),
      },

      // Payment
      {
        header: "Payment",
        Cell: ({ row }) => {
          const booking = row.original;
          const payment = booking.payment || {};
          const normalizedPaymentStatus = String(
            payment.paymentStatus || "",
          ).toLowerCase();
          const isPaymentSuccessful = ["success", "completed"].includes(
            normalizedPaymentStatus,
          );
          const isCancelled = isTerminalCancellation(booking);
          const paymentLabel = isPaymentSuccessful
            ? "Success"
            : isCancelled
              ? "-"
              : payment.paymentStatus || "Pending";
          return (
            <div>
              <div
                className={`badge ${
                  isPaymentSuccessful
                    ? "bg-success"
                    : isCancelled
                      ? "bg-secondary"
                      : "bg-warning text-dark"
                }`}
              >
                {paymentLabel}
              </div>
              <small className="d-block text-muted">
                {isCancelled && !isPaymentSuccessful
                  ? "-"
                  : payment.paymentMode || "-"}
              </small>
            </div>
          );
        },
      },

      // Booking Status

      {
        header: "Trip Status",
        Cell: ({ row }) => {
          const booking = row.original;

          if (isDriverRetry(booking)) {
            return <span className="badge bg-secondary">Pending</span>;
          }

          if (isTerminalCancellation(booking)) {
            return <span className="badge bg-danger">Cancelled</span>;
          }

          if (booking.hasEnded?.status) {
            return <span className="badge bg-primary">Completed</span>;
          }

          if (booking.hasStarted) {
            return <span className="badge bg-info text-dark">Ongoing</span>;
          }

          if (booking.tripReview?.required) {
            return (
              <span className="badge bg-warning text-dark">
                Review required
              </span>
            );
          }

          if (hasDriverReached(booking)) {
            return <span className="badge bg-secondary">Driver arrived</span>;
          }

          return <span className="badge bg-secondary">Pending</span>;
        },
      },
      {
        header: "Action",
        Cell: ({ row }) => {
          const booking = row.original;
          const canCancel =
            !isTerminalCancellation(booking) &&
            !booking.hasStarted &&
            !booking.hasEnded?.status;

          return canCancel ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => cancelBookingByAdmin(booking)}
            >
              Cancel trip
            </button>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
    ],
    [
      editingRowId,
      selectedDriver,
      driverOptions,
      driverOptionsById,
      cancelBookingByAdmin,
    ],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />
            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : "0px" }}
              >
                <div className="main-content-inner">
                  <ToastContainer position="top-right" autoClose={4500} />
                  <div className="main-content-wrap">
                    <div className="tf-section">
                      <div>
                        <div className="bookings-heading">
                          <div>
                            <p className="eyebrow">TRIP MANAGEMENT</p>
                            <h5>
                              {view === "upcoming"
                                ? "Upcoming bookings"
                                : view === "ongoing"
                                  ? "Ongoing bookings"
                                  : view === "completed"
                                    ? "Completed bookings"
                                    : "Cancelled bookings"}
                            </h5>

                            <span>
                              {view === "upcoming"
                                ? "Today’s rides first, followed by future scheduled rides."
                                : view === "ongoing"
                                  ? "Trips currently in progress."
                                  : "Latest records appear first."}
                            </span>
                          </div>
                        </div>

                        <div className="booking-table-toolbar">
                          <div className="booking-search">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search booking, user, phone, driver..."
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />
                          </div>

                          <div className="booking-tabs">
                            <Link
                              className={view === "upcoming" ? "active" : ""}
                              to="/bookings"
                            >
                              Upcoming
                            </Link>

                            <Link
                              className={view === "ongoing" ? "active" : ""}
                              to="/bookings/ongoing"
                            >
                              Ongoing
                            </Link>

                            <Link
                              className={view === "completed" ? "active" : ""}
                              to="/bookings/completed"
                            >
                              Completed
                            </Link>

                            <Link
                              className={view === "cancelled" ? "active" : ""}
                              to="/bookings/cancelled"
                            >
                              Cancelled
                            </Link>
                          </div>
                        </div>

                        {loading ? (
                          <div className="skeleton-table">
                            {[...Array(6)].map((_, i) => (
                              <div className="skeleton-row" key={i}>
                                <div className="skeleton skeleton-name"></div>
                                <div className="skeleton skeleton-status"></div>
                                <div className="skeleton skeleton-phone"></div>
                                <div className="skeleton skeleton-email"></div>
                                <div className="skeleton skeleton-status"></div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            <ReactTable
                              columns={columns}
                              data={filteredBookings}
                            />
                          </>
                        )}
                      </div>

                      {photoModalOpen && selectedBooking && (
                        <div
                          className="booking-photo-modal-overlay"
                          onClick={closePhotoModal}
                        >
                          <div
                            className="booking-photo-modal"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Header */}
                            <div className="booking-photo-modal-header">
                              <div className="booking-modal-header-content">
                                {/* Top row */}
                                <div className="booking-modal-title-row">
                                  <div>
                                    <div className="booking-photo-modal-eyebrow">
                                      BOOKING MEDIA
                                    </div>

                                    <h4>{selectedBooking.booking_id}</h4>
                                  </div>

                                  <div className="booking-modal-photo-count">
                                    <FaImages />
                                    {selectedBooking.carPhotos?.length ||
                                      0}{" "}
                                    {selectedBooking.carPhotos?.length === 1
                                      ? "photo"
                                      : "photos"}
                                  </div>
                                </div>

                                {/* Customer / Driver information */}
                                <div className="booking-modal-meta">
                                  <div className="booking-modal-meta-item">
                                    <span className="booking-modal-meta-label">
                                      USER
                                    </span>

                                    <strong>
                                      {selectedBooking.userDetails?.userName ||
                                        "N/A"}
                                    </strong>
                                  </div>

                                  <div className="booking-modal-meta-item">
                                    <span className="booking-modal-meta-label">
                                      PHONE
                                    </span>

                                    <strong>
                                      {selectedBooking.userDetails
                                        ?.phoneNumber || "N/A"}
                                    </strong>
                                  </div>

                                  <div className="booking-modal-meta-item">
                                    <span className="booking-modal-meta-label">
                                      DRIVER
                                    </span>

                                    <strong>
                                      {selectedBooking.approvedDriver
                                        ?.driverName || "Not assigned"}
                                    </strong>
                                  </div>

                                  {/* WhatsApp */}
                                  <button
                                    type="button"
                                    className="booking-whatsapp-btn"
                                    onClick={openWhatsApp}
                                  >
                                    <FaWhatsapp />
                                    WhatsApp
                                  </button>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="booking-photo-close"
                                onClick={closePhotoModal}
                              >
                                <FaTimes />
                              </button>
                            </div>

                            {/* Main preview */}
                            {selectedBooking.carPhotos?.length > 0 && (
                              <>
                                <div className="booking-photo-preview">
                                  <img
                                    src={
                                      selectedBooking.carPhotos[
                                        selectedPhotoIndex
                                      ]?.path
                                    }
                                    alt={
                                      selectedBooking.carPhotos[
                                        selectedPhotoIndex
                                      ]?.name || "Car"
                                    }
                                  />

                                  {selectedBooking.carPhotos.length > 1 && (
                                    <>
                                      <button
                                        type="button"
                                        className="booking-photo-nav booking-photo-prev"
                                        onClick={previousPhoto}
                                      >
                                        <FaChevronLeft />
                                      </button>

                                      <button
                                        type="button"
                                        className="booking-photo-nav booking-photo-next"
                                        onClick={nextPhoto}
                                      >
                                        <FaChevronRight />
                                      </button>
                                    </>
                                  )}

                                  <div className="booking-photo-counter">
                                    {selectedPhotoIndex + 1} /{" "}
                                    {selectedBooking.carPhotos.length}
                                  </div>
                                </div>

                                {/* Current photo info */}
                                <div className="booking-photo-current-info">
                                  <div className="booking-photo-filename">
                                    {
                                      selectedBooking.carPhotos[
                                        selectedPhotoIndex
                                      ]?.name
                                    }
                                  </div>

                                  <button
                                    type="button"
                                    className="booking-photo-download"
                                    disabled={
                                      downloadingPhoto === selectedPhotoIndex
                                    }
                                    onClick={() =>
                                      downloadPhoto(
                                        selectedBooking.carPhotos[
                                          selectedPhotoIndex
                                        ],
                                        selectedPhotoIndex,
                                      )
                                    }
                                  >
                                    <FaDownload />

                                    {downloadingPhoto === selectedPhotoIndex
                                      ? "Downloading..."
                                      : "Download"}
                                  </button>
                                </div>

                                {/* Thumbnails */}
                                <div className="booking-photo-thumbnails">
                                  {selectedBooking.carPhotos.map(
                                    (photo, index) => (
                                      <button
                                        type="button"
                                        key={`${photo.path}-${index}`}
                                        className={`booking-photo-thumbnail ${
                                          selectedPhotoIndex === index
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          setSelectedPhotoIndex(index)
                                        }
                                      >
                                        <img
                                          src={photo.path}
                                          alt={
                                            photo.name || `Photo ${index + 1}`
                                          }
                                        />

                                        <span>{index + 1}</span>
                                      </button>
                                    ),
                                  )}
                                </div>
                              </>
                            )}

                            {/* Footer */}
                            <div className="booking-photo-modal-footer">
                              <span>{selectedBooking.booking_id}</span>

                              <button
                                type="button"
                                className="booking-download-all"
                                disabled={downloadingAll}
                                onClick={downloadAllPhotos}
                              >
                                <FaDownload />

                                {downloadingAll
                                  ? "Downloading..."
                                  : "Download all"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Bookings;
